import Papa from "papaparse";
import { dibels_columns } from "../../../../utils/files";
import { LINKIT_COLUMN_MUST_HAVE, DOUBLE_HEADER_COLUMN_HAVE } from "../FileReaderHelpers";

/**
 * Handler for Linkit file format
 */
export class LinkitHandler {
  
  /**
   * Process Linkit CSV data with special header handling
   */
  static processLinkitData(
    originalData: string,
    columnsNames: string[],
    rawData: any[],
    updateDataCallback: (result: any, file: File) => void
  ): void {
    // Handle "Selected tests" format
    const firstCellValueForLinkIt = "Selected tests";
    if (columnsNames[0] === firstCellValueForLinkIt) {
      if (rawData.length > 3) {
        if (
          rawData[0][firstCellValueForLinkIt] === "Period" &&
          rawData[1][firstCellValueForLinkIt] === "Filters"
        ) {
          const dataWithoutUpperSection = this.skipLinkitHeaderRows(originalData, 4);
          this.parseLinkitData(dataWithoutUpperSection, updateDataCallback);
          return;
        }
      }
    }

    // Handle date-based format or complex headers
    const linkitConstants = ["Export Date", "Selected Tests"];
    const fifthCellValueForLinkIt = "Student";

    if (
      (rawData.length > 3 &&
        (rawData[3]?.[columnsNames[0]] === fifthCellValueForLinkIt ||
          rawData[4]?.[columnsNames[0]] === fifthCellValueForLinkIt)) ||
      (linkitConstants.indexOf(columnsNames[0]) !== -1 &&
        linkitConstants.indexOf(rawData[0][columnsNames[0]]) !== -1)
    ) {
      const processedData = this.processComplexLinkitFormat(originalData);
      this.parseLinkitData(processedData, updateDataCallback);
      return;
    }
  }

  /**
   * Skip initial rows in Linkit file
   */
  private static skipLinkitHeaderRows(originalData: string, rowsToSkip: number): string {
    let count = 0;
    let i = 0;
    for (; i < originalData.length; i++) {
      const char = originalData.charAt(i);
      if (char === "\n") {
        count++;
      }
      if (count === rowsToSkip) {
        break;
      }
    }
    return originalData.substring(i);
  }

  /**
   * Process complex Linkit format with multiple grades and double headers
   */
  private static processComplexLinkitFormat(originalData: string): string {
    const originalDataArr = originalData.split("\n");
    let singleRow: string[] = [];
    let columnsCount = 0;

    // Find the header row with student ID columns
    let i = 0;
    for (; i < originalDataArr.length; i++) {
      singleRow = originalDataArr[i].split(",");
      if (!columnsCount) columnsCount = singleRow.length;

      let matchCount = 0;
      for (let j = 0; j < singleRow.length; j++) {
        if (
          LINKIT_COLUMN_MUST_HAVE.indexOf(singleRow[j].toLocaleLowerCase()) !== -1
        )
          matchCount++;
      }
      if (matchCount === LINKIT_COLUMN_MUST_HAVE.length - 1) {
        break;
      }
    }

    originalDataArr.splice(0, i);

    // Fix multiple grades in one sheet
    const deleteOtherGradeData = this.identifyExtraGradeColumns(originalDataArr);
    if (deleteOtherGradeData.length > 0) {
      this.removeExtraGradeColumns(originalDataArr, deleteOtherGradeData);
      columnsCount -= deleteOtherGradeData.length;
    }

    let dataWithoutUpperSection = originalDataArr.join("\n");

    // Fix double headers
    dataWithoutUpperSection = this.fixDoubleHeaders(
      dataWithoutUpperSection,
      originalDataArr,
      columnsCount
    );

    return dataWithoutUpperSection;
  }

  /**
   * Identify columns that contain data for other grades
   */
  private static identifyExtraGradeColumns(originalDataArr: string[]): number[] {
    const deleteOtherGradeData: number[] = [];
    let dibelsHeaderFirstValue: string | undefined;

    for (let i = 0; i < originalDataArr.length; i++) {
      const singleRow = originalDataArr[i].split(",");
      for (let j = 0; j < singleRow.length; j++) {
        if (singleRow[j].includes("DIBELS")) {
          if (!dibelsHeaderFirstValue) {
            dibelsHeaderFirstValue = singleRow[j];
          }
          if (dibelsHeaderFirstValue && dibelsHeaderFirstValue !== singleRow[j]) {
            deleteOtherGradeData.push(j);
          }
        }
      }
    }

    return deleteOtherGradeData;
  }

  /**
   * Remove extra grade columns from data
   */
  private static removeExtraGradeColumns(originalDataArr: string[], deleteColumns: number[]): void {
    for (let i = 0; i < originalDataArr.length; i++) {
      const singleRow = originalDataArr[i].split(",");
      singleRow.splice(deleteColumns[0], deleteColumns.length);
      originalDataArr[i] = singleRow.join(",");
    }
  }

  /**
   * Fix double headers in Linkit files
   */
  private static fixDoubleHeaders(
    dataWithoutUpperSection: string,
    originalDataArr: string[],
    columnsCount: number
  ): string {
    const dataWithoutUpperSectionArr: string[] = dataWithoutUpperSection.split(",");
    let j = 2 * (columnsCount - 1);

    // Check if ID header exists
    const hasIDHeader = 
      dataWithoutUpperSectionArr[j] ||
      dataWithoutUpperSectionArr.includes('"ID"') ||
      dataWithoutUpperSectionArr.includes('"id"') ||
      dataWithoutUpperSectionArr.includes('ID') ||
      dataWithoutUpperSectionArr.includes('id');

    if (!hasIDHeader) {
      throw new Error("The ID Header is missing in the uploaded file. Please add ID Header.");
    }

    // Remove double headers if present
    if (
      DOUBLE_HEADER_COLUMN_HAVE.filter((double) => 
        double.some((col) => (originalDataArr[1]?.toLocaleLowerCase() || '').includes(col.toLowerCase()))
      ).length > 0) {
      for (; j >= 0; j--) {
        if (
          dataWithoutUpperSectionArr[j].includes("DIBELS") ||
          dataWithoutUpperSectionArr[j] === ""
        ) {
          dataWithoutUpperSectionArr.splice(j, 1);
        }
      }
    }

    return dataWithoutUpperSectionArr.join(",").replace('ORF (Accuracy)\n', 'ORF (Accuracy),');
  }

  /**
   * Parse Linkit data using Papa Parse
   */
  private static parseLinkitData(
    csvData: string,
    updateDataCallback: (result: any, file: File) => void
  ): void {
    Papa.parse(csvData, {
      complete: updateDataCallback,
      header: true,
      dynamicTyping: true,
      trimHeaders: true,
      skipEmptyLines: true,
      transform(value: string, field: string | number): any {
        return value.trim();
      },
    });
  }
}
